'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const tiles = [
  {
    icon: 'ChartBarIcon' as const,
    tag: '500+ ZAMÓWIEŃ DZIENNIE?',
    title: 'Rosnący wolumen przerasta ręczne procesy',
    body: 'Obsługujesz setki zamówień dziennie. Automatyzujemy kompletację, etykiety, synchronizację stanów i strukturę akcji w BaseLinker lub Apilo. Jeśli koszty systemu rosną - przygotowujemy bezpieczną migrację.',
    color: 'bg-blue-pale border-blue-DEFAULT/20',
    accent: 'text-blue-DEFAULT',
  },
  {
    icon: 'ExclamationTriangleIcon' as const,
    tag: 'Chaos wielokanałowy',
    title: 'Allegro, Amazon i sklep — trzy systemy, jeden chaos',
    body: 'Stany magazynowe nie zgadzają się między kanałami, zamówienia giną w zakładkach przeglądarki. Integrujemy wszystkie kanały w jednym panelu z synchronizacją w czasie rzeczywistym.',
    color: 'bg-amber-50 border-amber-200',
    accent: 'text-amber-600',
  },
  {
    icon: 'GlobeAltIcon' as const,
    tag: 'Amazon wielokrajowo',
    title: 'Sprzedajesz na DE, FR, IT i potrzebujesz faktur OSS',
    body: 'Sprzedaż cross-border generuje obowiązki VAT OSS w każdym kraju UE. Automatyzujemy wystawianie faktur z właściwymi stawkami VAT per kraj, bez ręcznej obsługi każdej transakcji.',
    color: 'bg-emerald-50 border-emerald-200',
    accent: 'text-emerald-600',
  },
];

export default function DlaKogoSection() {
  return (
    <section id="dla-kogo" className="py-14 md:py-20 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-3">
            <p className="text-xs font-700 uppercase tracking-wide text-blue-DEFAULT" style={{ fontFamily: 'Inter, sans-serif' }}>Dla kogo</p>
            <h2 className="text-4xl md:text-5xl font-700 tracking-tight text-navy leading-[1.0]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Skala rośnie. Procesy nie?
            </h2>
          </div>
          <p className="text-base text-muted font-400 max-w-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Porządkujemy sprzedaż wielokanałową i pomagamy przejść z BaseLinker do Apilo bez utraty automatyzacji i struktury danych.
          </p>
        </div>

        {/* Tiles — bento-style: first spans 2 cols on large screens */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiles.map((tile, i) => (
            <FeatureTile key={tile.tag} tile={tile} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureTile({ tile, index }: { tile: typeof tiles[0]; index: number }) {
  const { elementRef, isVisible } = useScrollAnimation();

  return (
    <div
      ref={elementRef}
      className={`rounded-2xl border p-10 flex flex-col gap-6 transition-all duration-700 ease-out hover:scale-105 hover:shadow-xl ${
        tile.color
      } ${
        index === 0 ? 'lg:col-span-1' : ''
      } ${
        isVisible
          ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${index * 150}ms`,
      }}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-white/70`}>
          <Icon name={tile.icon} size={20} className={tile.accent} variant="outline" />
        </div>
        <span className={`text-xs font-800 uppercase tracking-wide ${tile.accent}`} style={{ fontFamily: 'Inter, sans-serif' }}>
          {tile.tag}
        </span>
      </div>
      <h3 className="text-xl font-700 text-navy leading-snug tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
        {tile.title}
      </h3>
      <p className="text-sm text-muted font-400 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
        {tile.body}
      </p>
    </div>
  );
}